import { useState, useEffect, useRef } from "react";
import { X, Percent, Loader2, TrendingDown, Truck, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";

const signupSchema = z.object({
  full_name: z.string().trim().min(1, "Name is required").max(100),
  email: z.string().trim().email("Please enter a valid email").max(255),
  phone: z.string().trim().max(30).optional().or(z.literal("")),
});

const STORAGE_KEY = "deals_popup_dismissed";
const POPUP_DELAY_MS = 5000;

export default function DealsPopup() {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const formLoadedAt = useRef(Date.now());
  const honeypotRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (dismissed) return;

    const timer = setTimeout(() => {
      setOpen(true);
      formLoadedAt.current = Date.now();
    }, POPUP_DELAY_MS);

    return () => clearTimeout(timer);
  }, []);

  // Listen for manual open events (e.g. from header button)
  useEffect(() => {
    const handler = () => {
      setOpen(true);
      setSubmitted(false);
      formLoadedAt.current = Date.now();
    };
    window.addEventListener("open-deals-popup", handler);
    return () => window.removeEventListener("open-deals-popup", handler);
  }, []);

  const dismiss = () => {
    setOpen(false);
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});

    const formData = new FormData(e.currentTarget);
    const data = {
      full_name: formData.get("full_name") as string,
      email: formData.get("email") as string,
      phone: (formData.get("phone") as string) || "",
    };

    const result = signupSchema.safeParse(data);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) fieldErrors[err.path[0] as string] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setSubmitting(true);

    try {
      const { error } = await supabase.functions.invoke("submit-newsletter", {
        body: {
          full_name: result.data.full_name,
          email: result.data.email,
          phone: result.data.phone || null,
          page_url: window.location.href,
          website: honeypotRef.current?.value || "",
          form_loaded_at: formLoadedAt.current,
        },
      });

      if (error) throw error;

      setSubmitted(true);
      localStorage.setItem(STORAGE_KEY, Date.now().toString());

      setTimeout(() => setOpen(false), 3000);
    } catch {
      toast({
        title: "Something went wrong",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="relative w-full max-w-lg bg-card text-card-foreground rounded-2xl shadow-2xl border border-border overflow-hidden animate-in zoom-in-95 duration-300">

        {/* Header band */}
        <div className="bg-gradient-hero px-6 py-6 text-primary-foreground text-center">
          <div className="inline-flex items-center justify-center p-3 bg-accent rounded-full mb-3">
            <Percent className="h-7 w-7 text-accent-foreground" />
          </div>
          <h2 className="text-2xl font-bold tracking-tight mb-1">Exclusive Warehouse Deals</h2>
          <p className="text-primary-foreground/80 text-sm max-w-xs mx-auto">
            Get first access to blow-out pricing on pallet racking, beams, uprights & more.
          </p>

          {/* Value props */}
          <div className="flex items-center justify-center gap-4 mt-4 text-xs text-primary-foreground/70">
            <span className="flex items-center gap-1"><TrendingDown className="h-3.5 w-3.5 text-accent" /> Up to 50% off</span>
            <span className="flex items-center gap-1"><Truck className="h-3.5 w-3.5 text-accent" /> Nationwide</span>
            <span className="flex items-center gap-1"><Bell className="h-3.5 w-3.5 text-accent" /> First dibs</span>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          {submitted ? (
            <div className="text-center py-4">
              <div className="text-4xl mb-3">🎉</div>
              <p className="text-lg font-semibold text-foreground">You're on the list!</p>
              <p className="text-sm text-muted-foreground mt-1">We'll notify you the moment a great deal drops.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Honeypot - hidden from real users */}
              <input
                ref={honeypotRef}
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                className="absolute -left-[9999px] opacity-0 h-0 w-0"
                aria-hidden="true"
              />

              <div>
                <Label htmlFor="popup-name" className="text-sm font-medium">Name *</Label>
                <Input
                  id="popup-name"
                  name="full_name"
                  placeholder="Your full name"
                  className="mt-1"
                  required
                  maxLength={100}
                />
                {errors.full_name && <p className="text-xs text-destructive mt-1">{errors.full_name}</p>}
              </div>

              <div>
                <Label htmlFor="popup-email" className="text-sm font-medium">Email *</Label>
                <Input
                  id="popup-email"
                  name="email"
                  type="email"
                  placeholder="you@company.com"
                  className="mt-1"
                  required
                  maxLength={255}
                />
                {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
              </div>

              <div>
                <Label htmlFor="popup-phone" className="text-sm font-medium">Phone <span className="text-muted-foreground">(optional)</span></Label>
                <Input
                  id="popup-phone"
                  name="phone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  className="mt-1"
                  maxLength={30}
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                variant="accent"
                size="lg"
                className="w-full font-semibold text-base"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Signing Up...</>
                ) : (
                  <>🔥 Get Deal Alerts</>
                )}
              </Button>

              <p className="text-[11px] text-muted-foreground text-center leading-tight">
                No spam, ever. Only genuine deals. Unsubscribe anytime.
              </p>

              <button
                type="button"
                onClick={dismiss}
                className="w-full text-sm text-accent hover:text-accent/80 transition-colors mt-2 py-2 underline underline-offset-4 decoration-accent/40"
              >
                No thanks, I don't want great deals on warehouse equipment
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
